import {
  OrgCard,
  type OrgRollup,
} from '@/components/features/organisations/OrgCard'
import { PendingInvitationsPanel } from '@/components/features/organisations/invitations/PendingInvitationsPanel'
import {
  myInvitationsQueryKey,
  useMyInvitations,
} from '@/components/features/organisations/invitations/useMyInvitations'
import Modal from '@/components/layout/Modal'
import { useAuth } from '@/context/AuthContext'
import { useClient } from '@/context/ClientContext'
import { usePermissions } from '@/context/PermissionsContext'
import { useIsInSupportSession } from '@/context/SupportSessionContext'
import { useFormData } from '@/hooks/useFormData'
import {
  UseOffsetPaginationQueryOptions,
  useOffsetPaginationQuery,
} from '@/hooks/useOffsetPaginationQuery'
import { getTablePageSize } from '@/hooks/useUserPreferences'
import { fetchOrganisationsPaginated } from '@/lib/clientHelpers'
import { supportSessionOrgId } from '@/lib/support-session'
import { cn, formatBytes } from '@/lib/utils'
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { useMutation, useQueries } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { HeroPanel } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import { EmptyState } from '@droidmap/frontend-ui'
import { Input } from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'
import {
  Organisation,
  OrganisationServiceClient,
} from '@droidmap/organisation-service-contract'
import { ProjectServiceClient } from '@droidmap/project-service-contract'
import {
  DroidMapMetadata,
  OffsetPaged,
  StorageSummary,
} from '@droidmap/shared-service-contract'
import { serverRefusalMessage } from '@droidmap/shared-utils'

const PREFERENCE_KEY = 'USER_ORGANISATIONS'

export const Route = createFileRoute('/_authenticated/orgs/')({
  component: Organisations,
  beforeLoad: async ({ context }) => {
    // A support session works in one organisation, and is refused the
    // others: an assist session's DroidMap support is a member of none, and
    // a read-only one sees only the org it was started in. The list would
    // offer orgs that refuse to open (droidmap#2926).
    const sessionOrgId = supportSessionOrgId()
    if (sessionOrgId) {
      throw redirect({ to: '/orgs/$orgId', params: { orgId: sessionOrgId } })
    }
    const userId = await context.auth.getDroidmapId()
    const queryOptions: UseOffsetPaginationQueryOptions<
      Organisation,
      OffsetPaged<Organisation>
    > = {
      preferenceKey: PREFERENCE_KEY,
      queryKey: ['organisations', userId],
      queryFn: async (options) =>
        fetchOrganisationsPaginated(
          userId,
          context.client.organisationClient,
          context.metadata,
          options,
        ),
      initialPageSize: getTablePageSize(PREFERENCE_KEY),
    }

    return {
      orgsQueryOptions: queryOptions,
      userId,
    }
  },
  loader: async ({ context }) => {
    const { orgsQueryOptions, userId } = context
    const pageSize: number = orgsQueryOptions.initialPageSize ?? 10

    await Promise.all([
      // Warm the same cache entry `useOffsetPaginationQuery` will read on
      // mount: its key is [...queryKey, pageIndex, pageSize, resetKey].
      context.queryClient.fetchQuery({
        queryKey: [...orgsQueryOptions.queryKey, 0, pageSize, ''],
        queryFn: () => orgsQueryOptions.queryFn({ page: 0, pageSize }),
      }),
      // And the invitations (#3058), in parallel: an invitee with no orgs
      // must see their invitation, not the "create your first organisation"
      // screen, on first paint. `prefetchQuery` never throws — a failure
      // leaves the hook to retry and show its own error.
      context.queryClient.prefetchQuery({
        queryKey: myInvitationsQueryKey(userId),
        queryFn: async () =>
          (
            await context.client.organisationClient.listMyOrganisationInvitations(
              context.metadata,
            )
          ).items,
      }),
    ])

    return {
      metadata: context.metadata,
    }
  },
})

const totalStorageBytes = (s: StorageSummary | undefined): number => {
  if (!s) return 0
  return (Object.values(s) as unknown[]).reduce<number>(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )
}

/**
 * Fan out per-org queries to fetch project + member counts in parallel.
 * Returns a map keyed by orgId; values are undefined while still loading.
 */
function useOrgRollups(
  orgIds: string[],
  organisationClient: OrganisationServiceClient,
  projectClient: ProjectServiceClient,
  metadata: DroidMapMetadata,
): Map<string, OrgRollup | undefined> {
  const queries = useQueries({
    queries: orgIds.map((orgId) => ({
      queryKey: ['org-rollup', orgId],
      queryFn: async (): Promise<OrgRollup> => {
        // Scope metadata to this org for both calls — list permission checks
        // key off metadata.orgId server-side.
        const scoped = { ...metadata, orgId }
        // Both endpoints are offset-paginated and report the full match count
        // in `totalCount`. The rollup only needs the counts, so read them off a
        // single (smallest) page instead of walking every project/member — the
        // earlier cursor-based `processAllPages` had no `next` to follow here
        // and silently truncated both counts to the first 50.
        const [projects, members] = await Promise.all([
          projectClient.listProjectsByOrganisation(
            { orgId, pageSize: 1 },
            scoped,
          ),
          organisationClient.listOrganisationUsersByOrganisation(
            { orgId, pageSize: 1 },
            scoped,
          ),
        ])
        return {
          projectCount: projects.totalCount,
          memberCount: members.totalCount,
        }
      },
      staleTime: 60_000,
    })),
  })

  return useMemo(() => {
    const map = new Map<string, OrgRollup | undefined>()
    orgIds.forEach((orgId, i) => {
      map.set(orgId, queries[i]?.data)
    })
    return map
  }, [orgIds, queries])
}

interface CreateOrgCardProps {
  onClick: () => void
}

function CreateOrgCard({ onClick }: CreateOrgCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative flex min-h-[280px] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border-strong bg-transparent p-9 text-center transition-all duration-b ease-meridian',
        'hover:-translate-y-0.5 hover:border-accent-tint hover:bg-accent-soft',
        'focus-visible:outline-hidden focus-visible:border-accent-tint focus-visible:ring-2 focus-visible:ring-accent-soft',
      )}
    >
      <div
        className={cn(
          'grid h-13 w-13 place-items-center rounded-full border border-dashed border-border-strong text-text-dim transition-all duration-f ease-meridian',
          'group-hover:border-solid group-hover:border-accent group-hover:text-accent group-hover:bg-panel group-hover:shadow-[0_0_0_4px_var(--accent-soft)]',
        )}
      >
        <PlusIcon className="h-6 w-6" />
      </div>
      <div>
        <h3 className="text-text mb-1.5 text-[15px] font-semibold">
          Create organisation
        </h3>
        <p className="text-text-dim mx-auto max-w-[220px] text-xs leading-relaxed">
          Spin up a new workspace for a separate team, client, or project
          portfolio.
        </p>
      </div>
    </button>
  )
}

// Exported for unit testing; the route wires it up via `component` above.
export function Organisations() {
  const { userDetails } = useAuth()
  // Any support session is refused creating an organisation, so it is never
  // offered (droidmap#2926).
  const canCreate = !useIsInSupportSession()
  const [createItemOpen, setCreateItemOpen] = useState(false)
  const [filter, setFilter] = useState('')

  const [createState, handleCreateChange, resetCreateForm] = useFormData({
    orgName: '',
  })

  const navigate = useNavigate()
  const { organisationClient, projectClient } = useClient()
  const { refreshPermissions } = usePermissions()
  const { metadata, orgsQueryOptions, userId } = Route.useRouteContext()
  const myInvitations = useMyInvitations(userId, metadata)
  const hasInvitations = myInvitations.invitations.length > 0

  const {
    data: organisations,
    isLoading: isLoadingOrganisations,
    isError: isOrganisationsError,
    refetch: refetchOrganisations,
    refresh: refreshPagination,
  } = useOffsetPaginationQuery(orgsQueryOptions)

  // A failed fetch yields empty `data` too, so distinguish it from a genuine
  // empty list — otherwise a transient error would render the "first org"
  // welcome screen to a user who actually has organisations.
  const failedToLoadOrganisations =
    !isLoadingOrganisations &&
    isOrganisationsError &&
    organisations.length === 0

  // First-run state: the user belongs to no organisations yet. Since auto-
  // creation of a personal org on signup was removed (#2032), brand-new users
  // land here and need to be guided to create their first org rather than
  // shown a bare grid + filter toolbar.
  const hasNoOrganisations =
    !isLoadingOrganisations &&
    !isOrganisationsError &&
    organisations.length === 0

  // An invitee with no orgs yet (#3058): the invitation is the way in, so it
  // takes the place of the "create your first organisation" empty state rather
  // than sitting under a prompt to start a trial org of their own.
  const isInvitedFirstRun = hasNoOrganisations && hasInvitations
  // Until the invitations are known, a user with no orgs can't be told which
  // first-run they're in. Showing the create-your-own screen meanwhile is
  // exactly the trial-org detour the invited first-run exists to prevent.
  const isAwaitingInvitations = hasNoOrganisations && myInvitations.isLoading

  const orgIds = useMemo(() => organisations.map((o) => o.id), [organisations])
  const rollups = useOrgRollups(
    orgIds,
    organisationClient,
    projectClient,
    metadata,
  )

  const createMutation = useMutation({
    mutationFn: async (event: React.FormEvent) => {
      event.preventDefault()
      return organisationClient.createOrganisation(
        { name: createState.orgName },
        metadata,
      )
    },
    onSuccess: async (created) => {
      resetCreateForm()
      setCreateItemOpen(false)
      // Offset pagination has no client-side page cache to splice the new org
      // into — invalidate and let the server re-page, which also keeps
      // `totalCount` correct.
      refreshPagination()
      // Creating an org bootstraps the caller's own ORG_ADMIN grant server-side,
      // but the grants list is fetched once per session — without this refetch
      // the admin-only tabs (Members/Groups/Permissions/Billing/Settings) stay
      // hidden behind PermissionGate until a page reload. Refresh the list
      // first so the new org appears without waiting on the grants round-trip.
      await refreshPermissions()
      // An org born frozen awaiting activation (#2598 — the account had
      // already used its free trial) is a paid org that can't be used until
      // it's upgraded. Land the creator inside it, on the activation CTA,
      // rather than leaving them on the list to discover a locked card.
      if (created.lifecycleState === 'frozen') {
        await navigate({ to: '/orgs/$orgId', params: { orgId: created.id } })
      }
    },
  })

  const filtered = useMemo(() => {
    if (!filter.trim()) return organisations
    const needle = filter.toLowerCase()
    return organisations.filter(
      (o) =>
        o.name.toLowerCase().includes(needle) ||
        o.id.toLowerCase().includes(needle),
    )
  }, [organisations, filter])

  const stats = useMemo(() => {
    const totalProjects = organisations.reduce((sum, o) => {
      const r = rollups.get(o.id)
      return sum + (r?.projectCount ?? 0)
    }, 0)
    const totalStorage = organisations.reduce(
      (sum, o) => sum + totalStorageBytes(o.storageSummary),
      0,
    )
    return { totalProjects, totalStorage }
  }, [organisations, rollups])

  return (
    <div>
      <HeroPanel
        className="mb-7"
        eyebrow={
          <span className="inline-flex items-center gap-2.5">
            <span
              aria-hidden
              className="bg-accent inline-block h-1.5 w-1.5 rounded-full shadow-[0_0_10px_currentColor] animate-pulse"
            />
            Workspace · {userDetails.id ? 'Authenticated' : 'Loading'}
          </span>
        }
        title={
          hasNoOrganisations && canCreate
            ? 'Welcome to DroidMap'
            : hasNoOrganisations
              ? 'No organisations'
              : 'Set organisation'
        }
        description={
          isAwaitingInvitations
            ? undefined
            : isInvitedFirstRun
              ? 'You’ve been invited to join an organisation. Accept an invitation below to get started.'
              : hasNoOrganisations && canCreate
                ? 'You don’t belong to any organisations yet. Create your first one to start managing projects, members, and assets.'
                : hasNoOrganisations
                  ? 'This user belongs to no organisation yet.'
                  : canCreate
                    ? 'Select an organisation to enter, or create a new one to manage projects, members, and assets in a separate workspace.'
                    : 'Select an organisation to enter.'
        }
        actions={
          // Only summarise once the user actually has organisations — in the
          // zero-org first-run state these would just read 0 / 0 / 0 B beside
          // the welcome copy.
          organisations.length > 0 ? (
            <div className="flex items-end gap-9 pr-1">
              <div className="text-right">
                <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                  <em className="text-accent not-italic">
                    {organisations.length}
                  </em>
                </div>
                <div className="text-micro text-text-mute mt-2">
                  Organisations
                </div>
              </div>
              <div className="text-right">
                <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                  {stats.totalProjects.toLocaleString()}
                </div>
                <div className="text-micro text-text-mute mt-2">Projects</div>
              </div>
              <div className="text-right">
                <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                  {formatBytes(stats.totalStorage, 1)}
                </div>
                <div className="text-micro text-text-mute mt-2">Storage</div>
              </div>
            </div>
          ) : undefined
        }
      />

      <PendingInvitationsPanel
        invitations={myInvitations.invitations}
        isError={myInvitations.isError}
        onRetry={() => myInvitations.refetch()}
        onAccept={myInvitations.accept}
        onDecline={myInvitations.decline}
        pending={myInvitations.pending}
        actionError={myInvitations.actionError}
      />

      {(isLoadingOrganisations && organisations.length === 0) ||
      isAwaitingInvitations ? (
        <div className="flex justify-center py-16">
          <Spinner size="page" />
        </div>
      ) : failedToLoadOrganisations ? (
        /* A load failure must not be mistaken for "no organisations yet" —
           offer a retry instead of the first-run welcome screen. */
        <EmptyState
          icon={<BuildingOffice2Icon className="h-7 w-7" />}
          title="Couldn’t load your organisations"
          body="Something went wrong fetching your organisations. Please try again."
          action={
            <Button variant="outline" onClick={() => refetchOrganisations()}>
              Retry
            </Button>
          }
        />
      ) : isInvitedFirstRun ? (
        canCreate && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => setCreateItemOpen(true)}>
              <PlusIcon className="h-4 w-4" />
              Create your own organisation instead
            </Button>
          </div>
        )
      ) : hasNoOrganisations ? (
        /* First-run: guide the user to create their first organisation
           instead of showing the filter toolbar over an empty grid. */
        <EmptyState
          icon={<BuildingOffice2Icon className="h-7 w-7" />}
          title={
            canCreate
              ? 'Create your first organisation'
              : 'They belong to no organisation'
          }
          body={
            !canCreate ? (
              'A support session cannot create one.'
            ) : (
              <>
                <p>
                  Organisations are workspaces for your projects, members, and
                  assets. Create one to get started.
                </p>
                <p className="mt-2">
                  Every new organisation starts on the{' '}
                  <span className="text-text font-medium">
                    DroidMap Starter
                  </span>{' '}
                  plan, and you can upgrade any time after creating it.
                </p>
              </>
            )
          }
          action={
            canCreate ? (
              <Button onClick={() => setCreateItemOpen(true)}>
                <PlusIcon className="h-4 w-4" />
                Create organisation
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3.5">
            <div className="w-80 max-w-full">
              <Input
                id="org-filter"
                name="filter"
                type="text"
                placeholder="Filter organisations…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                leadingIcon={<MagnifyingGlassIcon className="h-3.5 w-3.5" />}
                className="h-10"
              />
            </div>
            {canCreate && (
              <Button onClick={() => setCreateItemOpen(true)}>
                <PlusIcon className="h-4 w-4" />
                New Organisation
              </Button>
            )}
          </div>

          {/* Section header */}
          <div className="border-border mb-3.5 flex items-baseline justify-between border-b pb-2.5">
            <h2 className="text-micro text-text-dim">All organisations</h2>
            <span className="text-text-mute font-mono text-[11px] uppercase tracking-wider">
              {filter
                ? `${filtered.length} of ${organisations.length}`
                : `${organisations.length} total`}
            </span>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
            {filtered.map((org) => (
              <OrgCard key={org.id} org={org} rollup={rollups.get(org.id)} />
            ))}
            {canCreate && (
              <CreateOrgCard onClick={() => setCreateItemOpen(true)} />
            )}
          </div>
        </>
      )}

      <Modal
        title="Create Organisation"
        icon={<PlusIcon />}
        isOpen={createItemOpen}
        onClose={() => {
          setCreateItemOpen(false)
          resetCreateForm()
        }}
      >
        <form onSubmit={createMutation.mutate} className="space-y-6">
          <Input
            label="Organisation Name"
            id="orgName"
            name="orgName"
            type="text"
            autoFocus={true}
            value={createState.orgName}
            onChange={handleCreateChange}
          />
          {createMutation.isError && (
            <div className="text-danger text-sm">
              {/* The server's own refusal when it sent one, and this page's
                  own copy otherwise. It used to render `error.message` raw,
                  which is never a sentence written for a user: a 5xx gave
                  "Failed to call api route POST /organisations - 500 Internal
                  Server Error", and a 409 the client's placeholder "Entity
                  already exists" (#2536).

                  Note what this does NOT cover: `createOrganisation`'s one
                  genuinely actionable refusal, "Verify your email address
                  before creating an organisation", is a `PermissionDeniedError`
                  → 403, which `serverRefusalMessage` deliberately excludes
                  (403 also carries "Permission denied", a literal as useless
                  as the one above). That gap predates this change — the raw
                  render never showed it either — and needs a 403 story of its
                  own rather than a widened gate. */}
              {serverRefusalMessage(createMutation.error) ??
                'Couldn’t create the organisation. Please try again in a moment.'}
            </div>
          )}
          <div className="mt-5 flex flex-row-reverse gap-2 sm:mt-4">
            <Button type="submit" isLoading={createMutation.isPending}>
              Submit
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateItemOpen(false)
                resetCreateForm()
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
